// Product Recipes - Connects Shopify products to ShiftSetGo recipes API.
// Renders archive-style recipe cards (image, stage badges, title, category
// pills, prep/serves meta) on product pages whose handle matches one or more
// recipes' shopifyRecipeTags.

class ProductRecipes {
    constructor() {
        this.apiBase = 'https://shiftsetgo.com/api/recipes/by-product';
        this.section = document.getElementById('product-recipes-section');
        this.recipesContainer = document.getElementById('recipes');
        this.init();
    }

    init() {
        if (!this.section || !this.recipesContainer) return;
        if (!window.location.pathname.includes('/products/')) return;
        this.loadRecipes();
    }

    getCurrentProductHandle() {
        const parts = window.location.pathname.split('/');
        const i = parts.indexOf('products');
        return i !== -1 && parts[i + 1] ? parts[i + 1] : null;
    }

    async loadRecipes() {
        const handle = this.getCurrentProductHandle();
        if (!handle) {
            this.removeSection();
            return;
        }

        try {
            const response = await fetch(`${this.apiBase}/${handle}`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const recipes = await response.json();
            if (Array.isArray(recipes) && recipes.length > 0) {
                this.renderRecipes(recipes);
                this.recipesContainer.style.display = 'block';
            } else {
                this.removeSection();
            }
        } catch (error) {
            console.error('Recipe fetch failed:', error);
            this.removeSection();
        }
    }

    removeSection() {
        if (this.section) this.section.remove();
    }

    /* ------------------------------------------------------------------ */
    /*  Render                                                             */
    /* ------------------------------------------------------------------ */

    renderRecipes(recipes) {
        this.renderDesktopGrid(recipes);
        this.renderMobileCarousel(recipes);
    }

    renderDesktopGrid(recipes) {
        const grid = this.recipesContainer.querySelector('.recipes-desktop');
        if (!grid) return;
        grid.innerHTML = recipes.map(r => this.createRecipeCardHTML(r)).join('');
    }

    renderMobileCarousel(recipes) {
        const track = this.recipesContainer.querySelector('.recipes-carousel-track');
        const dots = this.recipesContainer.querySelector('.recipes-carousel-dots');
        if (!track || !dots) return;

        track.innerHTML = recipes.map(r => this.createRecipeCardHTML(r)).join('');

        // Build dots
        dots.innerHTML = recipes
            .map((_, i) => `<div class="carousel-dot${i === 0 ? ' active' : ''}" data-slide="${i}"></div>`)
            .join('');

        // Dot clicks scroll the track to the matching card
        dots.querySelectorAll('.carousel-dot').forEach(dot => {
            dot.addEventListener('click', () => {
                const i = Number(dot.getAttribute('data-slide'));
                const card = track.children[i];
                if (card) card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
            });
        });

        // Update active dot as the user scrolls
        let scrollTimer;
        track.addEventListener('scroll', () => {
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => this.updateActiveDot(track, dots), 80);
        }, { passive: true });
    }

    updateActiveDot(track, dots) {
        const cards = track.children;
        if (!cards.length) return;
        const trackLeft = track.scrollLeft;
        let activeIndex = 0;
        for (let i = 0; i < cards.length; i++) {
            if (cards[i].offsetLeft - track.offsetLeft <= trackLeft + 20) {
                activeIndex = i;
            }
        }
        dots.querySelectorAll('.carousel-dot').forEach((dot, i) => {
            dot.classList.toggle('active', i === activeIndex);
        });
    }

    /* ------------------------------------------------------------------ */
    /*  Card markup                                                        */
    /* ------------------------------------------------------------------ */

    createRecipeCardHTML(recipe) {
        return `
            <a href="${recipe.permalink}" class="ssg-recipe-card" target="_blank" rel="noopener">
                <div class="ssg-recipe-card__image-wrap">
                    ${recipe.featured_image
                        ? `<img class="ssg-recipe-card__image" src="${recipe.featured_image}" alt="${this.escape(recipe.title)}" loading="lazy">`
                        : ''}
                    ${this.renderStageBadges(recipe.stages)}
                </div>
                <div class="ssg-recipe-card__content">
                    <h3 class="ssg-recipe-card__title">${this.escape(recipe.title)}</h3>
                    ${this.renderCategories(recipe.categories)}
                    ${this.renderMeta(recipe)}
                </div>
            </a>
        `;
    }

    renderStageBadges(stages) {
        if (!Array.isArray(stages) || stages.length === 0) return '';
        const pills = stages
            .map(s => `<span class="ssg-recipe-card__stage-badge">Stage ${this.escape(s)}</span>`)
            .join('');
        return `<div class="ssg-recipe-card__stages">${pills}</div>`;
    }

    renderCategories(categories) {
        if (!Array.isArray(categories) || categories.length === 0) return '';
        const pills = categories.slice(0, 3)
            .map(c => `<span class="ssg-recipe-card__cat">${this.escape(this.formatSlug(c))}</span>`)
            .join('');
        return `<div class="ssg-recipe-card__categories">${pills}</div>`;
    }

    renderMeta(recipe) {
        const parts = [];
        if (recipe.prepTime) {
            parts.push(`
                <span class="ssg-recipe-card__meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    ${this.escape(recipe.prepTime)}
                </span>
            `);
        }
        if (recipe.serves) {
            parts.push(`
                <span class="ssg-recipe-card__meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    Serves ${this.escape(recipe.serves)}
                </span>
            `);
        }
        if (!parts.length) return '';
        return `<div class="ssg-recipe-card__meta">${parts.join('')}</div>`;
    }

    /* ------------------------------------------------------------------ */
    /*  Utilities                                                          */
    /* ------------------------------------------------------------------ */

    formatSlug(s) {
        return String(s).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }

    escape(s) {
        return String(s).replace(/[&<>"']/g, c => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
        }[c]));
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new ProductRecipes();
});